
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { hasRouteAccess } from '../config/routePermissions';

const API_URL = process.env.REACT_APP_API_URL;
const RouteAccessContext = createContext();

export const RouteAccessProvider = ({ children }) => {
  const { caraUser, tprmUser, tprmUseruser, loading: authLoading } = useAuth();
  const [userCapabilities, setUserCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ⏳ Wait for AuthContext to finish initial check
    if (authLoading) {
      console.log('⏳ RouteAccessContext: Waiting for AuthContext to load...');
      setLoading(true);
      return;
    }

    const fetchUserCapabilities = async () => {
      console.log('🚀 Starting to fetch user capabilities...');
      setLoading(true);
      let capabilities = [];
      const userRole = (localStorage.getItem('role') || '').toUpperCase();

      try {
        // Call appropriate API based on logged-in user type
        if (caraUser?.admin_id) {
          console.log(' Fetching CARA user capabilities...');
          const response = await axios.get(`${API_URL}api/get_cara_login_users/`, {
            withCredentials: true
          });
          if (response.data?.[0]?.capabilities) {
            capabilities = response.data[0].capabilities;
            console.log('CARA capabilities loaded:', capabilities);
          }
        }

        if (tprmUser?.id || tprmUser?.user_id || tprmUser?.user_type) {
          console.log(' Fetching TPRM admin capabilities...');
          const response = await axios.get(`${API_URL}api/get_cara_tprm_users/`, {
            withCredentials: true
          });
          if (response.data?.[0]?.capabilities) {
            capabilities = [...capabilities, ...response.data[0].capabilities];
            console.log('TPRM admin capabilities loaded:', response.data[0].capabilities);
          }
        }

        if (tprmUseruser?.id || tprmUseruser?.user_id || tprmUseruser?.user_type) {
          console.log(' Fetching TPRM user capabilities...');
          const response = await axios.get(`${API_URL}api/get_cara_tprm_user/`, {
            withCredentials: true
          });
          if (response.data?.[0]?.capabilities) {
            capabilities = [...capabilities, ...response.data[0].capabilities];
            console.log('TPRM user capabilities loaded:', response.data[0].capabilities);
          }
        }

        if (userRole === 'VRM') {
          console.log(' Detected VRM session, assigning VRM capabilities...');
capabilities = [...capabilities, 'VRM Dashboard', 'Request Management', 'Chat Support', 'Feedback & Suggestions'];
        }       

        // Check for Superadmin
        if (userRole === 'SUPERADMIN' || userRole === 'INTERNALSTAFF') {
          console.log(' Fetching Superadmin capabilities...');
          try {
            const response = await axios.get(`${API_URL}api/get_superadmin_user/`, {
              withCredentials: true
            });
            if (response.data?.capabilities) {
              capabilities = [...capabilities, ...response.data.capabilities];
              console.log('Superadmin capabilities loaded:', response.data.capabilities);
            }
          } catch (err) {
            console.error('Error fetching Superadmin capabilities:', err);
          }
        }

        // Normalize capabilities (handle stringified JSON and nested objects)
        let normalizedCapabilities = [];

        // Helper function to extract strings from nested structures
        const extractCapabilities = (caps) => {
          if (typeof caps === 'string') {
            try {
              const parsed = JSON.parse(caps);
              extractCapabilities(parsed);
            } catch (e) {
              normalizedCapabilities.push(caps);
            }
          } else if (Array.isArray(caps)) {
            caps.forEach(extractCapabilities);
          } else if (typeof caps === 'object' && caps !== null) {
            Object.values(caps).forEach(extractCapabilities);
          }
        };

        extractCapabilities(capabilities);

        // Remove duplicates and set capabilities
        const uniqueCapabilities = [...new Set(normalizedCapabilities)];
        console.log('Setting user capabilities:', uniqueCapabilities);
        setUserCapabilities(uniqueCapabilities);
        console.log('🎯 Final user capabilities set, setting loading to false');

      } catch (error) {
        console.error('Error fetching user capabilities:', error);
        setUserCapabilities([]);
      } finally {
        console.log('🏁 Setting loading to false');
        setLoading(false);
      }
    };

    const localRole = localStorage.getItem('role');
    const hasAnyUser = caraUser?.admin_id
      || (tprmUser?.id || tprmUser?.user_id || tprmUser?.user_type)
      || (tprmUseruser?.id || tprmUseruser?.user_id || tprmUseruser?.user_type)
      || (localRole && localRole !== ''); // Trust ANY saved role as proof a user exists while React state loads

    if (hasAnyUser) {
      console.log('👤 User detected, fetching capabilities...');
      fetchUserCapabilities();
    } else {
      console.log('🚪 No user logged in, clearing capabilities');
      setUserCapabilities([]);
      setLoading(false);
    }
  }, [
    caraUser?.admin_id,
    tprmUser?.id, tprmUser?.user_id, tprmUser?.user_type,
    tprmUseruser?.id, tprmUseruser?.user_id, tprmUseruser?.user_type,
    authLoading
  ]);

  // Check if user has access to a specific route
  const hasAccess = (routePath) => hasRouteAccess(routePath, userCapabilities);

  // Get all routes accessible to current user
  const getAccessibleRoutes = () => {
    return Object.keys(require('../config/routePermissions').ROUTE_PERMISSIONS)
      .filter(route => hasAccess(route));
  };

  const value = {
    userCapabilities,
    hasAccess,
    getAccessibleRoutes,
    loading,
    // Refresh capabilities (useful if user permissions change)
    refreshCapabilities: () => {
      if (caraUser?.admin_id || tprmUser?.id || tprmUseruser?.id) {
        const fetchUserCapabilities = async () => {
          // ... same logic as above
        };
        fetchUserCapabilities();
      }
    }
  };

  return (
    <RouteAccessContext.Provider value={value}>
      {children}
    </RouteAccessContext.Provider>
  );
};

export const useRouteAccess = () => useContext(RouteAccessContext);

